import React, { InputHTMLAttributes } from "react";
import {
  Control,
  Controller,
  FieldValues,
  Path,
  RegisterOptions,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import CoreDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

interface DatePickerProps<FieldsType extends FieldValues> {
  name: Path<FieldsType>;
  control: Control<FieldsType>;
  rules?: RegisterOptions<FieldsType>;
  required?: boolean;
  label?: string;
  placeholder?: string;
  requiredMessage?: string;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  className?: string;
  wrapperClassName?: string;
  disabled?: boolean;
  disablePast?: boolean;
  disableFuture?: boolean;
}

function DatePicker<FieldsType extends FieldValues>({
  label,
  name,
  control,
  required,
  requiredMessage,
  placeholder,
  rules,
  disableFuture,
  disabled = false,
  disablePast = false,
}: DatePickerProps<FieldsType>) {
  const { t } = useTranslation();
  return (
    <Controller
      name={name}
      rules={{
        required: requiredMessage || (required && "Please select a date"),
        ...(rules || {}),
      }}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <div className="relative">
          <label className="text-base ml-1 mb-0.5 block" htmlFor={field?.name}>
            <span>
              {t(label)}
              {(required || requiredMessage) && (
                <span className="text-red-600 ml-0.5 text-base">*</span>
              )}
            </span>
          </label>
          <CoreDatePicker
            id={field.name}
            className="placeholder:text-gray-500 border-gray-200 bg-transparent rounded-lg w-full px-2.5 py-3 border focus:border-brand-secondary-hover
            focus-within:border-brand-secondary-hover focus:bg-[#eaf1fa]  transition-all focus:ring-0 focus-visible:ring-0 focus-visible:border-brand-secondary-hover  outline-none"
            placeholderText="DD-MM-YYYY"
            dateFormat="dd/MM/yyyy"
            wrapperClassName="w-full"
            minDate={disablePast ? new Date() : undefined}
            maxDate={disableFuture ? new Date() : undefined}
            disabledKeyboardNavigation={false}
            onChange={field.onChange}
            selected={field.value}
            disabled={disabled}
            customInput={<CustomInput inputRef={field.ref} />}
          />
          <p className="text-red-600 font-normal text-sm m-0 p-0 ml-1  bottom-0 left-0 right-0">
            {t(error?.message)}
          </p>
        </div>
      )}
    />
  );
}

const CustomInput = (props) => {
  const { inputRef } = props;
  return <input {...props} ref={inputRef} readOnly={true} />;
};

export default DatePicker;
